import { supabase } from './supabase';

export interface Snippet {
  id: string;
  business_id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Get all snippets for a business
 */
export async function getSnippets(businessId: string): Promise<Snippet[]> {
  const { data, error } = await supabase
    .from('snippets')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching snippets:', error);
    throw error;
  }

  return data || [];
}

/**
 * Create a new snippet
 */
export async function createSnippet(
  businessId: string,
  title: string,
  content: string
): Promise<Snippet> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('snippets')
    .insert({
      business_id: businessId,
      title: title.trim(),
      content: content.trim(),
      created_by: user?.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating snippet:', error);
    throw error;
  }

  return data;
}

/**
 * Update an existing snippet
 */
export async function updateSnippet(
  snippetId: string,
  updates: { title?: string; content?: string }
): Promise<Snippet> {
  const updateData: any = {};
  
  if (updates.title !== undefined) {
    updateData.title = updates.title.trim();
  }
  
  if (updates.content !== undefined) {
    updateData.content = updates.content.trim();
  }

  const { data, error } = await supabase
    .from('snippets')
    .update(updateData)
    .eq('id', snippetId)
    .select()
    .single();

  if (error) {
    console.error('Error updating snippet:', error);
    throw error;
  }

  return data;
}

/**
 * Delete a snippet
 */
export async function deleteSnippet(snippetId: string): Promise<void> {
  const { error } = await supabase
    .from('snippets')
    .delete()
    .eq('id', snippetId);

  if (error) {
    console.error('Error deleting snippet:', error);
    throw error;
  }
}

/**
 * Search snippets by title or content
 */
export async function searchSnippets(
  businessId: string,
  query: string
): Promise<Snippet[]> {
  const { data, error } = await supabase
    .from('snippets')
    .select('*')
    .eq('business_id', businessId)
    .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error searching snippets:', error);
    throw error;
  }

  return data || [];
}

